import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { brl, ptDate } from './finance-shared'
import { FiLoader, FiCreditCard, FiAlertCircle } from 'react-icons/fi'

// Extrato do Asaas — o que exige decisão humana.
//
// As TAXAS voltaram ao automático na tarde de 04/08. Saíram de manhã, a pedido
// do diretor (eram 102 lançamentos de R$ 1,33 em média entulhando o Contas a
// Pagar), e voltaram no mesmo dia porque puxar o extrato à mão toda semana é
// tarefa que ninguém lembra de fazer — e taxa esquecida some do DRE sem ninguém
// ter apagado nada. Entre o ruído e o esquecimento, escolheu-se o ruído: ele é
// visível, o outro não.
//
// Então esta tela deixou de ser "a fila de tudo" e virou "o que o sistema não
// tem como decidir sozinho". Na prática, o SAQUE (TRANSFER): não há regra que
// diga se é transferência entre contas da casa ou pagamento a terceiro. Ele tem
// botão de lançar, que abre o lançamento preenchido. Um Pix de R$ 280,00 de
// 30/06 ficou 5 semanas fora do sistema porque nada mostrava que existia.
//
// Recebimento de boleto e taxa já lançada aparecem esmaecidos, sem ação.
// Escondê-los faria a tela parecer incompleta em relação ao extrato do Asaas —
// e a pergunta "cadê o crédito de tal dia?" não teria resposta aqui.

interface Linha {
  id: number
  asaas_tx_id: string
  tx_type: string
  tx_date: string
  valor: number
  description: string | null
  lancavel: boolean
}

export function ExtratoAsaas({ onClose, onLancado, onLancarManual }: {
  onClose: () => void
  onLancado?: () => void
  // Chamado para a linha que NÃO é taxa (saque/TRANSFER): abre o lançamento
  // normal já preenchido, para a pessoa escolher natureza e categoria. Ver o
  // comentário do botão lá embaixo.
  onLancarManual?: (l: { tx_date: string; valor: number; description: string | null }) => void
}) {
  const { authenticatedFetch } = useAuth()
  const [linhas, setLinhas] = useState<Linha[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [salvando, setSalvando] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setErro(null)
    try {
      const r = await authenticatedFetch('/api/asaas/extrato')
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao carregar')
      setLinhas(d.itens || []); setSel(new Set())
    } catch (e: any) { setErro(e.message) }
    finally { setLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { load() }, [load])

  const lancaveis = linhas.filter(l => l.lancavel)
  const toggle = (id: number) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })
  const todas = lancaveis.length > 0 && lancaveis.every(l => sel.has(l.id))
  const toggleTodas = () => setSel(todas ? new Set() : new Set(lancaveis.map(l => l.id)))
  const totalSel = lancaveis.filter(l => sel.has(l.id)).reduce((a, l) => a + Math.abs(l.valor), 0)

  const lancar = async () => {
    if (!sel.size || salvando) return
    setSalvando(true); setErro(null)
    try {
      const r = await authenticatedFetch('/api/asaas/extrato/lancar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...sel] }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao lançar')
      window.alert(`${d.lancados} lançamento(s) criado(s), somando ${brl(d.valor)}.`
        + (d.ignorados ? `\n${d.ignorados} linha(s) ignorada(s) — já lançadas ou não lançáveis.` : '')
        + (d.erros?.length ? `\n⚠ ${d.erros.length} erro(s) — ver logs do backend.` : ''))
      await load()
      onLancado?.()
    } catch (e: any) { setErro(e.message) }
    finally { setSalvando(false) }
  }

  return (
    <SideModal
      title="Extrato Asaas"
      subtitle="Taxas entram sozinhas; saque espera você dizer o que é"
      icon={FiCreditCard}
      onClose={onClose}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex items-center gap-3">
          <div className="text-[13px] text-muted-foreground flex-1">
            {sel.size > 0
              ? <>{sel.size} selecionada(s) · <span className="font-medium text-foreground">{brl(totalSel)}</span></>
              : 'Taxas já entram sozinhas. Aqui ficam os saques, que precisam de decisão.'}
          </div>
          <button onClick={onClose} className="neu-convex h-9 px-3.5 rounded-lg border border-border text-sm hover:bg-secondary">Fechar</button>
          <button onClick={lancar} disabled={!sel.size || salvando}
            className="neu-relief h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2">
            {salvando && <FiLoader className="w-4 h-4 animate-spin" />}
            Lançar selecionadas
          </button>
        </div>
      }>

      {/* PLANOS (21/10/2026): a lista mora numa caixa de papel com cabeçalho
          (título + contagem; "marcar todas" no canto), não solta na gaveta. */}
      {erro && <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">{erro}</div>}

      <section className="neu-card rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap px-4 py-3 border-b border-border/60">
          <h4 className="text-[15px] font-semibold text-foreground">Linhas do extrato</h4>
          {!loading && linhas.length > 0 && (
            <span className="text-[13px] text-muted-foreground">{linhas.length} linha(s)</span>
          )}
          {!loading && lancaveis.length > 0 && (
            <label className="ml-auto flex items-center gap-2 text-[13px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={todas} onChange={toggleTodas} className="accent-primary" />
              Marcar as {lancaveis.length} linha(s) lançável(is)
            </label>
          )}
        </div>
      {loading ? (
        <div className="py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></div>
      ) : linhas.length === 0 ? (
        <div className="py-10 px-4 text-center text-muted-foreground text-sm">Nada esperando decisão no extrato do Asaas.</div>
      ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2.5 pl-4 w-10" />
                <th className="py-2.5 font-medium w-24">Data</th>
                <th className="py-2.5 font-medium">Descrição</th>
                <th className="py-2.5 pr-4 font-medium w-28 text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(l => (
                <tr key={l.id} className={`border-b border-border/50 last:border-0 ${l.lancavel ? '' : 'opacity-45'}`}>
                  <td className="py-2.5 pl-4">
                    {l.lancavel ? (
                      <input type="checkbox" checked={sel.has(l.id)} onChange={() => toggle(l.id)} className="accent-primary" />
                    ) : l.tx_type === 'TRANSFER' ? (
                      // Saque NÃO tem regra automática: pode ser transferência pra
                      // conta da casa ou Pix pra terceiro — só quem conhece a
                      // operação sabe. Antes ficava aqui com um ícone inerte, e foi
                      // assim que um Pix de R$ 280,00 de 30/06 passou 5 semanas fora
                      // do sistema: o ÚNICO item que exigia gente era justamente o
                      // que a tela mostrava como não-acionável (auditoria 04/08).
                      <button onClick={() => onLancarManual?.(l)} title="Lançar à mão: escolher se é despesa ou transferência, e a categoria"
                        className="neu-relief text-xs rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 px-1.5 py-0.5 font-semibold whitespace-nowrap">
                        lançar
                      </button>
                    ) : (
                      <span title="Recebimento de boleto: já entrou como receita pelo título quando o cliente pagou. Lançar de novo dobraria o valor.">
                        <FiAlertCircle className="w-3.5 h-3.5 text-muted-foreground/60" />
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 whitespace-nowrap text-muted-foreground">{ptDate(l.tx_date)}</td>
                  <td className="py-2.5">
                    <div className="text-foreground">{l.description || `Taxa Asaas ${l.tx_type}`}</div>
                    <div className="text-xs text-muted-foreground">{l.tx_type}</div>
                  </td>
                  <td className={`py-2.5 pr-4 text-right tabular-nums whitespace-nowrap ${l.valor < 0 ? 'text-foreground' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {brl(Math.abs(l.valor))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      )}
      </section>
    </SideModal>
  )
}
